import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { getMyCurrentPoints } from "../../api/rankingService";
import Loader from "../../common/Loader";
import Toast from "../../common/Toast";
import useFansUnitedSDK from "../../hooks/useFansUnitedSDK";
import { InfoCardWrapper, SeasonEnd, SuccessModal } from "./components";
import CurrentGameweek from "./components/CurrentGameweek";
import PastGameweek from "./components/PastGameweek";
import RoundSelector from "./components/RoundSelector/RoundSelector";
import styles from "./PredictionsFragment.module.css";
import { getCurrentGroup, getMatchesForGroup, isSeasonEnded } from "./PredictionsHelpers";
import { usePredictionScores, usePredictionsData, useSubmitPredictions } from "./utils";
import { calculateTimeLeft, findEarliestKickoff, formatDateForDeadline } from "./utils/timeHelpers";

const PredictionsFragment = ({ goToPrizes }) => {
	const dispatch = useDispatch();
	const fansUnitedSDK = useFansUnitedSDK();

	// Local state
	const [toastMessage, setToastMessage] = useState(null);
	const [selectedGroupId, setSelectedGroupId] = useState("1");
	const [activeGroupId, setActiveGroupId] = useState("1");
	const [isInfoShown, setIsInfoShown] = useState(!localStorage.getItem("infoCardHidden"));
	const [showSuccessModal, setShowSuccessModal] = useState(false);

	// Custom hooks for data and state management
	const { matches, availableMarkets, userPredictions, summary, groups, isLoading, getData } = usePredictionsData(fansUnitedSDK);

	// Determine current group and game ID for ranking
	const currentGroup = getCurrentGroup(groups);

	useEffect(() => {
		if (!currentGroup) return;
		setSelectedGroupId(currentGroup?.groupId);
		setActiveGroupId(currentGroup?.groupId);
	}, [currentGroup]);

	// Memoize filtered matches to prevent unnecessary recalculations
	const filteredMatches = useMemo(() => getMatchesForGroup(matches, groups, selectedGroupId), [matches, groups, selectedGroupId]);

	const kickOff = filteredMatches?.length ? new Date(findEarliestKickoff(filteredMatches)) : undefined;
	const deadline = kickOff ? formatDateForDeadline(kickOff) : "TBD";
	const timeleft = kickOff ? calculateTimeLeft(kickOff) : { days: "00", hours: "00", minutes: "00" };

	// A group is considered "past" if it comes before the current active group in the sequence
	const selectedGroupIndex = groups.findIndex((g) => g.groupId === selectedGroupId);
	const currentGroupIndex = groups.findIndex((g) => g.groupId === currentGroup?.groupId);
	const isSelectedGroupPast = selectedGroupIndex !== -1 && currentGroupIndex !== -1 && selectedGroupIndex < currentGroupIndex;

	// Check if the season has ended
	const seasonEnded = isSeasonEnded(groups);
	// const seasonEnded = true;

	// Check if predictions can still be made (deadline hasn't passed)
	const canMakePredictions = kickOff && new Date() < kickOff;

	// Check if user has made predictions for the current filtered matches
	const matchIds = filteredMatches?.map((match) => match.id) || [];
	const hasPredictionsForCurrentMatches = userPredictions.some((prediction) => matchIds.includes(prediction.matchId));

	const { currentScores, isButtonActive, hasSubmitted, hasChangedSinceSubmission, handleScoreChange, handleSuccessfulSubmission } =
		usePredictionScores(filteredMatches, userPredictions);

	const { submitAllPredictions, isSubmitting } = useSubmitPredictions(dispatch, fansUnitedSDK, filteredMatches, currentScores);

	// Effects
	useEffect(() => {
		if (matches.length > 0) return;
		if (!fansUnitedSDK) return;

		getData().catch((error) => {
			setToastMessage({
				text: error.data?.error?.message || "Error!",
				type: "error",
			});
		});
	}, [fansUnitedSDK, getData, matches.length]);

	// Submit handler that uses the custom hook
	const handleSubmit = async () => {
		const result = await submitAllPredictions();
		if (result.success) {
			handleSuccessfulSubmission();
			setShowSuccessModal(true);
		} else {
			setToastMessage({ text: result.error, type: "error" });
		}
	};

	// Handler for closing the success modal
	const handleCloseSuccessModal = () => {
		setShowSuccessModal(false);
	};

	const currentPoints = getMyCurrentPoints(userPredictions, filteredMatches, availableMarkets);

	// Utility function to smoothly scroll to matches list
	const scrollToMatchesList = () => {
		const matchesListElement = document.getElementById("matches-list");

		if (matchesListElement) {
			// Find the scrollable container (.cardsContainerSafari)
			const scrollContainer = matchesListElement.closest(`.${styles.cardsContainerSafari}`);

			if (scrollContainer) {
				// Calculate the position relative to the scroll container
				const containerRect = scrollContainer.getBoundingClientRect();
				const elementRect = matchesListElement.getBoundingClientRect();
				const relativeTop = elementRect.top - containerRect.top;
				// Buffer to account for fixed navigation that overlaps the container
				const navigationBuffer = 10;

				// Scroll the container to show the element with buffer
				const targetScroll = scrollContainer.scrollTop + relativeTop - navigationBuffer;

				scrollContainer.scrollTo({
					top: targetScroll,
					behavior: "smooth",
				});
			}
		}
	};

	return (
		<div className={styles.container}>
			<div className={styles.cardsContainerSafari}>
				{!seasonEnded && (
					<RoundSelector
						groups={groups}
						selectedGroupId={selectedGroupId}
						setSelectedGroupId={setSelectedGroupId}
						activeGroupId={activeGroupId}
					/>
				)}

				{isLoading || matches.length === 0 ? (
					<Loader />
				) : (
					<>
						<InfoCardWrapper
							isShown={isInfoShown}
							hasMatches={true}
							onClose={() => {
								setIsInfoShown(false);
								localStorage.setItem("infoCardHidden", true);
							}}
							onPlayPress={scrollToMatchesList}
							onPrizesPress={goToPrizes}
						/>

						{seasonEnded ? (
							<SeasonEnd />
						) : isSelectedGroupPast ? (
							<PastGameweek
								filteredMatches={filteredMatches}
								availableMarkets={availableMarkets}
								summary={summary}
								userPredictions={userPredictions}
								currentScores={currentScores}
								fansUnitedSDK={fansUnitedSDK}
								hasPredictionsForCurrentMatches={hasPredictionsForCurrentMatches}
								currentPoints={currentPoints}
								deadline={deadline}
								selectedGroupId={selectedGroupId}
							/>
						) : (
							<CurrentGameweek
								filteredMatches={filteredMatches}
								availableMarkets={availableMarkets}
								summary={summary}
								userPredictions={userPredictions}
								currentScores={currentScores}
								onScoreChange={handleScoreChange}
								fansUnitedSDK={fansUnitedSDK}
								canMakePredictions={canMakePredictions}
								timeleft={timeleft}
								deadline={deadline}
								isButtonActive={isButtonActive}
								isSubmitting={isSubmitting}
								hasSubmitted={hasSubmitted}
								hasChangedSinceSubmission={hasChangedSinceSubmission}
								onSubmit={handleSubmit}
							/>
						)}
					</>
				)}
			</div>

			{toastMessage && <Toast message={toastMessage} />}

			<SuccessModal isOpen={showSuccessModal} onClose={handleCloseSuccessModal} currentScores={currentScores} />
		</div>
	);
};

export default PredictionsFragment;
